import React, { useEffect, useMemo, useState } from "react";
import {
  Plus, Trash2, Save, Bell, CheckCircle2, Pause, Play, Ban,
  Sparkles, Zap, ArrowRightLeft,
} from "lucide-react";
import { toast } from "sonner";
import {
  useBillingSettings, useTenantSubs, useMyOrders, useBillingNotifications,
  useBillingEvents, useBillingActions,
} from "@/hooks/useBilling";
import { SubStatusBadge } from "@/components/settings/SubStatusBadge";
import { FormDrawer } from "@/components/kit/FormDrawer";
import { TextField, SelectField, Field } from "@/components/kit/fields";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { fmtMoney, fmtDate, fmtDateTime } from "@/lib/format";

const EXPIRY_OPTS = [
  { value: "paywall", label: "Block with paywall (only Billing accessible)" },
  { value: "notify_tenant", label: "Keep working, notify the tenant" },
  { value: "notify_admin", label: "Keep working, notify Super Admin only" },
];
const PERIODS = [
  { value: "trial", label: "Trial" }, { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" }, { value: "semi_annual", label: "Semi-Annual" },
  { value: "yearly", label: "Yearly" }, { value: "lifetime", label: "Lifetime" },
  { value: "custom", label: "Custom" },
];
const SUB_TABS = ["Subscriptions", "Payments", "Orders", "Subscription History", "Renewal History", "Failed Payments"];
const RENEWAL_TYPES = ["payment", "extend", "force_renew", "convert_trial", "change_plan"];

export function BillingSuperAdmin() {
  const { data: settings } = useBillingSettings();
  const { data: subsData } = useTenantSubs();
  const { data: ordersData } = useMyOrders();
  const { data: notifData } = useBillingNotifications();
  const { data: eventsData } = useBillingEvents();
  const { saveSettings, markRead } = useBillingActions();
  const [form, setForm] = useState(null);
  const [manage, setManage] = useState({ open: false, tenant: null });
  const [review, setReview] = useState({ open: false, order: null });
  const [tab, setTab] = useState("Subscriptions");

  useEffect(() => { if (settings) setForm(JSON.parse(JSON.stringify(settings))); }, [settings]);

  const subs = subsData?.items || [];
  const orders = ordersData?.items || [];
  const events = eventsData?.items || [];
  const notifs = notifData?.items || [];
  const unread = notifData?.unread || 0;
  const payments = useMemo(() => orders.filter((o) => o.status === "paid"), [orders]);
  const failed = useMemo(() => orders.filter((o) => ["failed", "rejected", "expired"].includes(o.status)), [orders]);
  const renewals = useMemo(() => events.filter((e) => RENEWAL_TYPES.includes(e.type)), [events]);

  if (!form) return <div className="text-sm text-muted-foreground">Loading billing settings…</div>;

  const setPlan = (i, k, v) => setForm((f) => { const plans = [...f.plans]; plans[i] = { ...plans[i], [k]: v }; return { ...f, plans }; });
  const addPlan = () => setForm((f) => ({ ...f, plans: [...f.plans, { id: `plan-${Date.now()}`, name: "New plan", period: "monthly", price: 0, currency: "USDT", active: true, trial_days: 0, grace_days: 0, description: "", features: [] }] }));
  const rmPlan = (i) => setForm((f) => ({ ...f, plans: f.plans.filter((_, x) => x !== i) }));

  const save = async () => {
    await saveSettings.mutateAsync({
      plans: form.plans.map((p) => ({
        ...p, price: Number(p.price) || 0,
        trial_days: Number(p.trial_days) || 0, grace_days: Number(p.grace_days) || 0,
        custom_days: p.period === "custom" ? (Number(p.custom_days) || 30) : p.custom_days,
        features: typeof p.features === "string" ? p.features.split(",").map((s) => s.trim()).filter(Boolean) : p.features,
      })),
      free_trial_days: Number(form.free_trial_days) || 0,
      expiry_action: form.expiry_action,
    });
    toast.success("Billing settings saved");
  };

  const subColumns = [
    { key: "name", label: "Organization", sortable: true, render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "owner", label: "Owner", exportValue: (r) => r.owner?.email, render: (r) => <span className="text-muted-foreground">{r.owner?.email || "—"}</span> },
    { key: "plan_name", label: "Plan", sortable: true, render: (r) => r.plan_name || "—" },
    { key: "period", label: "Period", render: (r) => <span className="capitalize">{(r.period || "—").replace("_", " ")}</span> },
    { key: "status", label: "Status", sortable: true, render: (r) => <SubStatusBadge status={r.status} /> },
    { key: "days_left", label: "Days left", align: "right", mono: true, sortable: true, sortValue: (r) => r.days_left, render: (r) => <span className={r.days_left <= 5 ? "text-[#EF4444]" : ""}>{r.days_left}</span> },
    { key: "current_period_end", label: "Expires", sortable: true, sortValue: (r) => r.current_period_end || "", render: (r) => r.current_period_end ? fmtDate(r.current_period_end) : "—" },
  ];
  const subFilters = [{ key: "status", label: "Status", getValue: (r) => r.status, options: ["active", "trial", "paused", "cancelled", "expired", "none"].map((v) => ({ value: v, label: v })) }];

  const orderColumns = [
    { key: "order_id", label: "Order", mono: true, render: (r) => <span className="text-muted-foreground">{(r.order_id || "").slice(0, 8)}</span> },
    { key: "tenant_name", label: "Tenant", sortable: true, render: (r) => r.tenant_name || r.tenant_id },
    { key: "plan_name", label: "Plan" },
    { key: "amount", label: "Amount", align: "right", mono: true, sortable: true, sortValue: (r) => Number(r.amount || 0), exportValue: (r) => r.amount, render: (r) => fmtMoney(r.amount, r.currency) },
    { key: "network", label: "Network" },
    { key: "status", label: "Status", sortable: true, render: (r) => <SubStatusBadge status={r.status} /> },
    { key: "created_at", label: "Created", sortable: true, render: (r) => fmtDate(r.created_at) },
  ];
  const eventColumns = [
    { key: "created_at", label: "Date", sortable: true, render: (r) => fmtDateTime(r.created_at) },
    { key: "tenant_name", label: "Tenant", sortable: true, render: (r) => r.tenant_name || r.tenant_id },
    { key: "type", label: "Event", sortable: true, render: (r) => <span className="rounded bg-white/[0.05] px-2 py-0.5 text-xs capitalize text-foreground/90">{(r.type || "").replace("_", " ")}</span> },
    { key: "detail", label: "Detail", render: (r) => <span className="text-muted-foreground">{r.detail}</span> },
    { key: "by", label: "By", render: (r) => r.by },
  ];

  return (
    <div className="space-y-6" data-testid="billing-super-admin">
      {notifs.length > 0 && (
        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid="billing-alerts">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5"><Bell className="h-4 w-4 text-[#EAB308]" /><h3 className="text-sm font-medium text-foreground">Alerts {unread > 0 && <span className="ml-1 rounded-full bg-[#EF4444] px-1.5 text-[10px] text-white">{unread}</span>}</h3></div>
            {unread > 0 && <button onClick={() => markRead.mutate()} data-testid="mark-read-btn" className="text-xs text-muted-foreground hover:text-foreground">Mark all read</button>}
          </div>
          <div className="space-y-1.5">
            {notifs.slice(0, 5).map((n) => (
              <div key={n.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className={`h-1.5 w-1.5 rounded-full ${n.read ? "bg-white/20" : "bg-[#EF4444]"}`} />
                <span className="text-foreground/90">{n.title}</span> — {n.body} <span className="text-xs">· {fmtDate(n.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Plans */}
      <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-medium text-foreground">Subscription plans</h3>
          <Button onClick={addPlan} variant="outline" data-testid="add-plan-btn" className="h-8 gap-1.5 border-white/15 text-xs"><Plus className="h-3.5 w-3.5" /> Add plan</Button>
        </div>
        <div className="space-y-3">
          {form.plans.map((p, i) => (
            <div key={p.id} className="rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-4" data-testid={`plan-row-${i}`}>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-12">
                <div className="sm:col-span-3"><Field label="Name"><Input value={p.name} onChange={(e) => setPlan(i, "name", e.target.value)} className="h-9 border-white/10 bg-[#141417] text-sm" data-testid={`plan-name-${i}`} /></Field></div>
                <div className="sm:col-span-3"><SelectField label="Period" value={p.period} onChange={(v) => setPlan(i, "period", v)} options={PERIODS} testId={`plan-period-${i}`} /></div>
                <div className="sm:col-span-2"><Field label="Price"><Input type="number" value={p.price} onChange={(e) => setPlan(i, "price", e.target.value)} className="h-9 border-white/10 bg-[#141417] text-sm" data-testid={`plan-price-${i}`} /></Field></div>
                <div className="sm:col-span-2"><Field label="Currency"><Input value={p.currency} onChange={(e) => setPlan(i, "currency", e.target.value)} className="h-9 border-white/10 bg-[#141417] text-sm" /></Field></div>
                <div className="sm:col-span-2 flex items-end justify-between gap-2 pb-1">
                  <div className="flex items-center gap-2"><Switch checked={p.active !== false} onCheckedChange={(v) => setPlan(i, "active", v)} data-testid={`plan-active-${i}`} /><span className="text-xs text-muted-foreground">{p.active !== false ? "Active" : "Off"}</span></div>
                  <button onClick={() => rmPlan(i)} className="text-muted-foreground hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-12">
                <div className="sm:col-span-2"><Field label="Trial days"><Input type="number" value={p.trial_days ?? 0} onChange={(e) => setPlan(i, "trial_days", e.target.value)} className="h-9 border-white/10 bg-[#141417] text-sm" /></Field></div>
                <div className="sm:col-span-2"><Field label="Grace days"><Input type="number" value={p.grace_days ?? 0} onChange={(e) => setPlan(i, "grace_days", e.target.value)} className="h-9 border-white/10 bg-[#141417] text-sm" /></Field></div>
                {p.period === "custom" && <div className="sm:col-span-2"><Field label="Custom days"><Input type="number" value={p.custom_days ?? 30} onChange={(e) => setPlan(i, "custom_days", e.target.value)} className="h-9 border-white/10 bg-[#141417] text-sm" /></Field></div>}
                <div className={cn("sm:col-span-6", p.period === "custom" ? "" : "sm:col-span-8")}><Field label="Description"><Input value={p.description || ""} onChange={(e) => setPlan(i, "description", e.target.value)} className="h-9 border-white/10 bg-[#141417] text-sm" placeholder="Short plan description" /></Field></div>
              </div>
              <div className="mt-3"><Field label="Features & limits (comma separated)"><Input value={Array.isArray(p.features) ? p.features.join(", ") : (p.features || "")} onChange={(e) => setPlan(i, "features", e.target.value)} className="h-9 border-white/10 bg-[#141417] text-sm" placeholder="Unlimited offers, Priority support, 10 users" /></Field></div>
            </div>
          ))}
        </div>
      </div>

      {/* Trial + expiry */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-6"><TextField label="Default free trial (days)" type="number" value={form.free_trial_days} onChange={(v) => setForm((f) => ({ ...f, free_trial_days: v }))} testId="trial-days" /></div>
        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-6"><SelectField label="When subscription expires" value={form.expiry_action} onChange={(v) => setForm((f) => ({ ...f, expiry_action: v }))} options={EXPIRY_OPTS} testId="expiry-action" /></div>
      </div>

      <Button onClick={save} disabled={saveSettings.isPending} data-testid="billing-save-settings" className="gap-2 bg-white text-black hover:bg-white/90"><Save className="h-4 w-4" /> Save billing settings</Button>

      {/* Tabs */}
      <div className="no-scrollbar flex flex-wrap gap-1 overflow-x-auto border-b border-white/[0.08]">
        {SUB_TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`billing-tab-${t.toLowerCase().replace(/ /g, "-")}`}
            className={cn("relative whitespace-nowrap px-3 py-2 text-sm transition-colors", tab === t ? "text-foreground" : "text-muted-foreground hover:text-foreground")}>
            {t}{tab === t && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-white" />}
          </button>
        ))}
      </div>

      {tab === "Subscriptions" && <DataTablePro testId="tenant-subs-table" columns={subColumns} rows={subs} filters={subFilters} searchKeys={["name"]} searchPlaceholder="Search tenants..." exportName="subscriptions" onRowClick={(r) => setManage({ open: true, tenant: r })} emptyText="No tenants." />}
      {tab === "Payments" && <DataTablePro testId="payments-table" columns={orderColumns} rows={payments} searchKeys={["tenant_name", "plan_name"]} searchPlaceholder="Search payments..." exportName="payments" onRowClick={(r) => setReview({ open: true, order: r })} emptyText="No payments yet." />}
      {tab === "Orders" && <DataTablePro testId="orders-table" columns={orderColumns} rows={orders} filters={[{ key: "status", label: "Status", getValue: (r) => r.status, options: ["pending", "paid", "failed", "expired", "needs_tx_hash", "rejected"].map((v) => ({ value: v, label: v })) }]} searchKeys={["tenant_name", "plan_name"]} searchPlaceholder="Search orders..." exportName="orders" onRowClick={(r) => setReview({ open: true, order: r })} emptyText="No payment orders yet." />}
      {tab === "Subscription History" && <DataTablePro testId="sub-history-table" columns={eventColumns} rows={events} filters={[{ key: "type", label: "Event", getValue: (r) => r.type, options: ["change_plan", "extend", "pause", "resume", "cancel", "start_trial", "end_trial", "convert_trial", "force_renew", "payment"].map((v) => ({ value: v, label: v.replace("_", " ") })) }]} searchKeys={["tenant_name", "detail"]} searchPlaceholder="Search history..." exportName="subscription-history" emptyText="No subscription events yet." />}
      {tab === "Renewal History" && <DataTablePro testId="renewal-history-table" columns={eventColumns} rows={renewals} searchKeys={["tenant_name", "detail"]} searchPlaceholder="Search renewals..." exportName="renewal-history" emptyText="No renewals yet." />}
      {tab === "Failed Payments" && <DataTablePro testId="failed-payments-table" columns={orderColumns} rows={failed} searchKeys={["tenant_name", "plan_name"]} searchPlaceholder="Search failed payments..." exportName="failed-payments" onRowClick={(r) => setReview({ open: true, order: r })} emptyText="No failed payments." />}

      <ManageTenantDrawer open={manage.open} tenant={manage.tenant} plans={form.plans} onOpenChange={(o) => setManage((m) => ({ ...m, open: o }))} />
      <OrderReviewDrawer open={review.open} order={review.order} onOpenChange={(o) => setReview((r) => ({ ...r, open: o }))} />
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, testId, danger, primary }) {
  return (
    <Button onClick={onClick} data-testid={testId} variant={primary ? "default" : "outline"}
      className={cn("h-9 justify-start gap-2 text-xs", primary ? "bg-white text-black hover:bg-white/90" : "border-white/15", danger && "border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10")}>
      <Icon className="h-4 w-4" /> {label}
    </Button>
  );
}

function ManageTenantDrawer({ open, tenant, plans, onOpenChange }) {
  const { tenantAction } = useBillingActions();
  const [planId, setPlanId] = useState("");
  const [days, setDays] = useState("");
  const [endDate, setEndDate] = useState("");
  useEffect(() => { if (open) { setPlanId(tenant?.plan_id || ""); setDays(""); setEndDate(""); } }, [open, tenant]);
  if (!tenant) return null;

  const act = async (body, msg) => { try { await tenantAction.mutateAsync({ tid: tenant.internalId, body }); toast.success(msg); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const paused = tenant.status === "paused";
  const isTrial = tenant.status === "trial";

  return (
    <FormDrawer open={open} onOpenChange={onOpenChange} title="Manage subscription" subtitle={tenant.name} size="lg" hideFooter testId="manage-tenant-drawer">
      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Plan"><div className="text-sm text-foreground">{tenant.plan_name || "—"}</div></Field>
          <Field label="Status"><SubStatusBadge status={tenant.status} /></Field>
          <Field label="Days left"><div className="text-sm text-foreground">{tenant.days_left}</div></Field>
        </div>

        {/* Change plan */}
        <div className="space-y-3 rounded-xl border border-white/[0.08] bg-[#141417] p-4">
          <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Change plan</h4>
          <div className="flex items-end gap-2">
            <div className="flex-1"><SelectField value={planId} onChange={setPlanId} options={plans.map((p) => ({ value: p.id, label: `${p.name} · ${p.price} (${p.period.replace("_", " ")})` }))} testId="manage-plan-select" /></div>
            <Button onClick={() => planId && act({ action: "change_plan", plan_id: planId }, "Plan changed")} data-testid="manage-change-plan-btn" className="h-10 bg-white text-black hover:bg-white/90">Apply</Button>
          </div>
        </div>

        {/* Quick extend */}
        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-4">
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Quick extend</h4>
          <div className="flex flex-wrap gap-2">
            {plans.filter((p) => p.period !== "trial").map((p) => (
              <Button key={p.id} variant="outline" data-testid={`extend-${p.period}`} onClick={() => act({ action: "extend", period: p.period }, `Extended by 1 ${p.period}`)} className="h-9 border-white/15 text-xs capitalize">+1 {p.period.replace("_", " ")}</Button>
            ))}
          </div>
          <div className="mt-3 flex items-end gap-2">
            <TextField label="Add days" type="number" value={days} onChange={setDays} testId="extend-days-input" className="flex-1" />
            <Button onClick={() => days && act({ action: "extend", days: Number(days) }, `Extended by ${days} days`)} data-testid="extend-days-btn" className="h-10 bg-white text-black hover:bg-white/90">Apply</Button>
          </div>
        </div>

        {/* Lifecycle */}
        <div className="space-y-3 rounded-xl border border-white/[0.08] bg-[#141417] p-4">
          <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Lifecycle</h4>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {paused
              ? <ActionBtn icon={Play} label="Resume" testId="action-resume" onClick={() => act({ action: "resume" }, "Subscription resumed")} />
              : <ActionBtn icon={Pause} label="Pause" testId="action-pause" onClick={() => act({ action: "pause" }, "Subscription paused")} />}
            <ActionBtn icon={Zap} label="Force Renew" testId="action-force-renew" onClick={() => act({ action: "force_renew" }, "Force-renewed")} />
            <ActionBtn icon={Sparkles} label="Start Trial" testId="action-start-trial" onClick={() => act({ action: "start_trial", plan_id: planId }, "Trial started")} />
            {isTrial && <ActionBtn icon={ArrowRightLeft} label="Convert → Paid" testId="action-convert-trial" onClick={() => act({ action: "convert_trial", plan_id: planId }, "Converted to paid")} />}
            {isTrial && <ActionBtn icon={Ban} label="End Trial" testId="action-end-trial" onClick={() => act({ action: "end_trial" }, "Trial ended")} />}
            <ActionBtn icon={Ban} danger label="Cancel" testId="action-cancel" onClick={() => act({ action: "cancel" }, "Subscription cancelled")} />
          </div>
        </div>
      </div>
    </FormDrawer>
  );
}

function OrderReviewDrawer({ open, order, onOpenChange }) {
  const { approveOrder, rejectOrder, expireOrder } = useBillingActions();
  const [tx, setTx] = useState("");
  useEffect(() => { if (open) setTx(order?.tx_hash || ""); }, [open, order]);
  if (!order) return null;
  const done = ["paid", "rejected", "expired"].includes(order.status);

  const wrap = (fn, msg) => async () => { try { await fn(); toast.success(msg); onOpenChange(false); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const rows = [
    ["Order ID", order.order_id], ["Tenant", order.tenant_name || order.tenant_id],
    ["Plan", order.plan_name], ["Amount", fmtMoney(order.amount, order.currency)],
    ["Network", order.network], ["Wallet", order.wallet_address], ["Provider", order.provider_type],
    ["TX hash", order.tx_hash || "—"], ["Created", fmtDate(order.created_at)],
    ["Expires", fmtDate(order.expires_at)], ["Paid at", order.paid_at ? fmtDate(order.paid_at) : "—"],
  ];

  return (
    <FormDrawer open={open} onOpenChange={onOpenChange} title="Payment order" subtitle={order.tenant_name || order.tenant_id} size="lg" hideFooter testId="order-review-drawer">
      <div className="space-y-5">
        <div className="flex items-center gap-2"><SubStatusBadge status={order.status} testId="review-status" />{order.fail_reason && <span className="text-xs text-[#EAB308]">{order.fail_reason}</span>}</div>
        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-4">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-start justify-between gap-4 border-b border-white/[0.04] py-2 text-sm last:border-0">
              <span className="text-muted-foreground">{k}</span><span className="break-all text-right font-mono text-xs text-foreground">{String(v)}</span>
            </div>
          ))}
        </div>
        {!done && (
          <div className="space-y-3 rounded-xl border border-white/[0.08] bg-[#141417] p-4">
            <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Manual review</h4>
            <TextField label="TX hash (optional, recorded on approval)" value={tx} onChange={setTx} testId="review-txhash" />
            <div className="flex flex-wrap gap-2">
              <Button onClick={wrap(() => approveOrder.mutateAsync({ id: order.order_id, tx_hash: tx }), "Order approved — subscription extended")} data-testid="review-approve-btn" className="gap-1.5 bg-[#22C55E] text-black hover:bg-[#22C55E]/90"><CheckCircle2 className="h-4 w-4" /> Approve</Button>
              <Button onClick={wrap(() => rejectOrder.mutateAsync({ id: order.order_id, reason: "Rejected after review" }), "Order rejected")} variant="outline" data-testid="review-reject-btn" className="border-white/15 text-xs">Reject</Button>
              <Button onClick={wrap(() => expireOrder.mutateAsync(order.order_id), "Order marked expired")} variant="outline" data-testid="review-expire-btn" className="border-white/15 text-xs">Mark expired</Button>
            </div>
          </div>
        )}
      </div>
    </FormDrawer>
  );
}
